import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Box, Button, CircularProgress, Stack, useMediaQuery } from '@mui/material'
import { MainBox, CommentsButton, SideBox, StackStyle } from './style'
import { PureLightTheme } from '../../../styles/theme'
import { NotFound } from '../../../components/ui-kit/NotFound'
import { SocialOrderAccessRestrictedStub } from '../../../components/SocialOrder/SocialOrderAccessRestrictedStub'
import { SocialOrderDescription } from '../../../components/SocialOrder/SocialOrderDescription'
import { SocialOrderInfo } from '../../../components/SocialOrder/SocialOrderInfo'
import { CommentsWrapper } from '../../../components/SocialOrder/CommentsWrapper'
import { CommentsDrawer } from '../../../components/ui-kit/CommentsDrawer'
import { IconComments } from '../../../icons/IconComments'
import { commentsButtonPwaSx } from '../../../consts/mobileLayout'
import { usePwaMobile } from '../../../hooks/usePwaMobile'
import { useGetSocialOrderQuery, useTrackSocialOrderViewMutation } from '../../../api/socialOrders'
import { resolveSocialOrderCommentsTotal } from '../../../api/types/socialOrders'
import { formatNumber } from '../../../common/FormatNumber'
import {
  COMMENT_ID_QUERY_KEY,
  COMMENT_TAB_QUERY_KEY,
  DISCUSSION_QUERY_KEY,
  DISCUSSION_QUERY_VALUE,
  NOTIF_SCROLL_SEQ_QUERY_KEY,
  PARENT_COMMENT_ID_QUERY_KEY,
  SCROLL_QUERY_KEY,
  SCROLL_QUERY_VALUE,
} from '../../../consts/commentNotificationQueryKeys'
import {
  parseCommentTabParam,
  parsePositiveIntParam,
  stripNotificationCommentParams,
} from '../../../utils/commentNotificationLink'
import { extractApiErrorMessage, isSocialOrderAccessRestrictedError } from '../../../utils/api'

export const SocialOrder = () => {
  useLayoutEffect(() => {
    document.title = 'Социальный заказ'
  }, [])

  const isTablet = useMediaQuery(PureLightTheme.breakpoints.down('md'))
  const isPwaMobile = usePwaMobile()
  const { id } = useParams<{ id: string }>()
  const cacheKey = id ?? ''
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const {
    data: socialOrder,
    error: socialOrderError,
    isLoading,
    isFetching,
    isError,
  } = useGetSocialOrderQuery(
    { id: cacheKey },
    { skip: !cacheKey, refetchOnMountOrArgChange: true },
  )
  const [trackSocialOrderView] = useTrackSocialOrderViewMutation()
  const [fetchSettled, setFetchSettled] = useState(false)

  const isAccessRestricted = isSocialOrderAccessRestrictedError(socialOrderError)

  useEffect(() => {
    setFetchSettled(false)
  }, [cacheKey])

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setFetchSettled(true)
    }
  }, [isLoading, isFetching, cacheKey])

  const [openComments, setOpenComments] = useState(false)
  const discussionFromQueryAppliedRef = useRef(false)
  const desktopCommentsScrollFromQueryAppliedRef = useRef(false)
  const commentsSectionRef = useRef<HTMLDivElement | null>(null)
  const commentsCount = resolveSocialOrderCommentsTotal(socialOrder?.counters.comments ?? 0)

  useEffect(() => {
    discussionFromQueryAppliedRef.current = false
    desktopCommentsScrollFromQueryAppliedRef.current = false
  }, [cacheKey])

  useEffect(() => {
    if (!socialOrder?.id) return
    void trackSocialOrderView({ socialOrderId: socialOrder.id, cacheKey })
  }, [socialOrder?.id, cacheKey, trackSocialOrderView])

  useEffect(() => {
    if (isLoading || !socialOrder) return
    if (!isTablet) return
    if (searchParams.get(DISCUSSION_QUERY_KEY) !== DISCUSSION_QUERY_VALUE) {
      discussionFromQueryAppliedRef.current = false
      return
    }
    if (discussionFromQueryAppliedRef.current) return
    discussionFromQueryAppliedRef.current = true
    setOpenComments(true)
  }, [isLoading, socialOrder, isTablet, searchParams])

  const notificationCommentIdFromQuery = parsePositiveIntParam(
    searchParams.get(COMMENT_ID_QUERY_KEY),
  )

  useEffect(() => {
    if (isLoading || !socialOrder) return
    if (isTablet) return
    if (searchParams.get(SCROLL_QUERY_KEY) !== SCROLL_QUERY_VALUE) return

    const isNotificationScroll = notificationCommentIdFromQuery != null

    if (!isNotificationScroll) {
      if (desktopCommentsScrollFromQueryAppliedRef.current) return
      desktopCommentsScrollFromQueryAppliedRef.current = true
    }

    const stripScrollOnly = !isNotificationScroll

    let cancelled = false
    let stripDone = false
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    const supportsScrollEnd = typeof window !== 'undefined' && 'onscrollend' in window
    let scrollEndHandler: EventListener | undefined

    const stripQuery = () => {
      if (!stripScrollOnly || stripDone || cancelled) return
      stripDone = true
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId)
        timeoutId = undefined
      }
      if (scrollEndHandler !== undefined) {
        window.removeEventListener('scrollend', scrollEndHandler)
        scrollEndHandler = undefined
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete(SCROLL_QUERY_KEY)
          return next
        },
        { replace: true },
      )
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return
        commentsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })

        if (stripScrollOnly) {
          if (supportsScrollEnd) {
            scrollEndHandler = () => {
              stripQuery()
            }
            window.addEventListener('scrollend', scrollEndHandler, { once: true })
          }
          timeoutId = setTimeout(stripQuery, 800)
        }
      })
    })

    return () => {
      cancelled = true
      if (timeoutId !== undefined) clearTimeout(timeoutId)
      if (scrollEndHandler !== undefined) {
        window.removeEventListener('scrollend', scrollEndHandler)
      }
      if (!stripDone) {
        desktopCommentsScrollFromQueryAppliedRef.current = false
      }
    }
  }, [
    isLoading,
    socialOrder,
    isTablet,
    searchParams,
    setSearchParams,
    notificationCommentIdFromQuery,
  ])

  const notificationLinkProps = useMemo(
    () => ({
      initialCommentTab: parseCommentTabParam(searchParams.get(COMMENT_TAB_QUERY_KEY)),
      notificationParentCommentId: parsePositiveIntParam(
        searchParams.get(PARENT_COMMENT_ID_QUERY_KEY),
      ),
      notificationCommentId: parsePositiveIntParam(searchParams.get(COMMENT_ID_QUERY_KEY)),
    }),
    [searchParams],
  )

  const handleNotificationDeepLinkConsumed = useCallback(() => {
    discussionFromQueryAppliedRef.current = false
    setSearchParams((prev) => stripNotificationCommentParams(prev), { replace: true })
  }, [setSearchParams])

  const handleOpenComments = () => setOpenComments(true)

  const handleCloseComments = () => {
    setOpenComments(false)
    discussionFromQueryAppliedRef.current = false
    const hasDrawerQuery =
      searchParams.has(DISCUSSION_QUERY_KEY) ||
      searchParams.has(COMMENT_TAB_QUERY_KEY) ||
      searchParams.has(PARENT_COMMENT_ID_QUERY_KEY) ||
      searchParams.has(COMMENT_ID_QUERY_KEY) ||
      searchParams.has(SCROLL_QUERY_KEY) ||
      searchParams.has(NOTIF_SCROLL_SEQ_QUERY_KEY)
    if (!hasDrawerQuery) return
    const next = stripNotificationCommentParams(searchParams)
    const search = next.toString()
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true },
    )
  }

  if (!fetchSettled || isLoading || isFetching) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%">
        <CircularProgress />
      </Stack>
    )
  }

  if (isAccessRestricted) {
    return <SocialOrderAccessRestrictedStub message={extractApiErrorMessage(socialOrderError)} />
  }

  if (isError || !socialOrder) {
    return <NotFound text="Социальный заказ не найден" />
  }

  return (
    <>
      <Stack direction="row" sx={StackStyle}>
        <Stack direction="column" sx={MainBox}>
          <SocialOrderDescription socialOrder={socialOrder} />
          {!isTablet && (
            <Box
              ref={commentsSectionRef}
              sx={{
                width: '100%',
                minWidth: 0,
                scrollMarginTop: 80,
              }}
            >
              <CommentsWrapper
                socialOrderId={socialOrder.id}
                cacheKey={cacheKey}
                socialOrder={socialOrder}
                {...notificationLinkProps}
                commentsSectionRootRef={commentsSectionRef}
                onNotificationDeepLinkConsumed={handleNotificationDeepLinkConsumed}
              />
            </Box>
          )}
          {isTablet && (
            <Button
              variant="action"
              disableRipple
              startIcon={<IconComments fontSize="small" />}
              onClick={handleOpenComments}
              sx={[CommentsButton, isPwaMobile && commentsButtonPwaSx]}
            >
              Дискуссия {formatNumber(commentsCount)}
            </Button>
          )}
        </Stack>
        {!isTablet && (
          <Box sx={SideBox}>
            <SocialOrderInfo socialOrder={socialOrder} />
          </Box>
        )}
      </Stack>
      <CommentsDrawer open={openComments} onClose={handleCloseComments}>
        <CommentsWrapper
          socialOrderId={socialOrder.id}
          cacheKey={cacheKey}
          socialOrder={socialOrder}
          onClose={handleCloseComments}
          {...notificationLinkProps}
          onNotificationDeepLinkConsumed={handleNotificationDeepLinkConsumed}
        />
      </CommentsDrawer>
    </>
  )
}
